import { FC, createContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { httpPost, httpPut } from 'src/axios/axiosUtils';
import {
  fotrgetPasswordURL,
  loginURL,
  refreshTokenReq,
  signUpURL,
  emailVerificationURL,
  clients as clientsURL,
  updatePasswordURL,
  users,
  magicLinkLoginURL
} from 'src/axios/requests';
import { setSession } from 'src/pages/Auth/helpers/tokens';
import { AuthContextValue, AuthProviderProps, AuthState } from './types';
import { reducer } from './reducer';
import {
  LoginEvent,
  RegistrationEvent
} from 'src/hooks/useAnalytics/AnalyticsEvent';
import { AccountType } from 'src/pages/Auth/helpers/tokens';
import { User } from 'src/models/user';

export const initialAuthState: AuthState = {
  user: {
    accountType: '',
    isSuccessful: false,
    message: '',
    token: { accessToken: '', expiresIn: '', refreshToken: '' },
    userId: null
  },
  compData: null
};

const AuthContext = createContext<AuthContextValue>({
  ...initialAuthState,
  method: 'JWT',
  login: () => Promise.resolve(),
  loginWithGoogle: (tokenId: string) => Promise.resolve(),
  magicLinkLogin: (token: string) => Promise.resolve(),
  registerWithGoogle: (tokenId: string) => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  forgetPassword: () => Promise.resolve(),
  updatePassword: () => Promise.resolve(),
  userVerification: () => Promise.resolve(),
  createUserClient: () => Promise.resolve(),
  toggleWelcomeScreen: () => {},
  refreshUserToken: () => Promise.resolve(),
  setData: () => {},
  setPreventBack: () => {}
});

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialAuthState);

  useEffect(() => {
    const initialize = async (): Promise<void> => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');
        const refreshToken = window.localStorage.getItem('refreshToken');
        const user = JSON.parse(window.localStorage.getItem('user'));

        if (accessToken) {
          setSession(accessToken, refreshToken, user);
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user: user
            }
          });
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null
            }
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
      }
    };

    initialize();
  }, []);

  const refreshUserToken = async () => {
    const response = await httpPut(
      refreshTokenReq,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('refreshToken')}`
        },
        data: {
          userId: state.user.userId,
          refreshToken: state.user.token.refreshToken
        }
      }
    );
    const user: User = response;
    setSession(user.token.accessToken, user.token.refreshToken, user);
    dispatch({
      type: 'LOGIN',
      payload: {
        user
      }
    });
  };

  const login = async (email: string, password: string): Promise<void> => {
    const response = await httpPost(loginURL, { email, password });
    const user: User = response;
    setSession(user.token.accessToken, user.token.refreshToken, user);
    dispatch({
      type: 'LOGIN',
      payload: {
        user
      }
    });
  };

  const magicLinkLogin = async (token: string): Promise<void> => {
    const response = await httpPost(magicLinkLoginURL, { token });
    const { accessToken, refreshToken, user } = response;
    setSession(accessToken, refreshToken, user);
    const loginGoogleEvent = new LoginEvent('magic_link_login', {
      email: user.email,
      organization: user.client.name,
      organization_id: user.client.id
    });
    dispatch({
      type: 'LOGIN',
      payload: {
        user
      }
    });
    return response;
  };

  const loginWithGoogle = async (tokenId: string): Promise<void> => {
    const response = await httpPost(loginURL, { oauthToken: tokenId });
    const { accessToken, refreshToken, user } = response;
    setSession(accessToken, refreshToken, user);
    const loginGoogleEvent = new LoginEvent('google', {
      email: user.email,
      organization: user.client.name,
      organization_id: user.client.id
    });
    dispatch({
      type: 'LOGIN',
      payload: {
        user
      }
    });
    return response;
  };

  const registerWithGoogle = async (tokenId: string): Promise<void> => {
    try {
      const response = await httpPost(signUpURL, { oauthToken: tokenId });
      const { accessToken, refreshToken, user } = response;
      setSession(accessToken, refreshToken, user);
      const registerGoogleEvent = new RegistrationEvent('google', {
        email: user.email
      });
      dispatch({
        type: 'REGISTER',
        payload: {
          user
        }
      });
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const logout = async (): Promise<void> => {
    setSession(null, null, null);
    dispatch({ type: 'LOGOUT' });
  };

  const forgetPassword = async (email: string): Promise<void> => {
    await httpPost(fotrgetPasswordURL, { email });
    dispatch({ type: 'FORGET_PASSWORD' });
  };
  const updatePassword = async (
    token: string,
    updatePasswordBody: { password: string; confirmPassword: string }
  ): Promise<void> => {
    await httpPut(`${updatePasswordURL}?token=${token}`, updatePasswordBody);
    dispatch({ type: 'UPDATE_PASSWORD' });
  };

  const userVerification = async (token: string): Promise<void> => {
    const response = await httpPost(
      `${emailVerificationURL}?token=${token}`,
      {}
    );
    const { accessToken, refreshToken, user } = response;
    const registerVerifiedEvent = new RegistrationEvent('verified', {
      email: user.email
    });
    setSession(accessToken, refreshToken, user);
    dispatch({
      type: 'REGISTER',
      payload: {
        user
      }
    });
  };

  const register = async (body): Promise<void> => {
    const user: User = await httpPost(signUpURL, body);
    setSession(user.token.accessToken, user.token.refreshToken, user);
    dispatch({
      type: 'REGISTER',
      payload: {
        user
      }
    });
  };

  const toggleWelcomeScreen = (toggled: boolean) => {
    dispatch({
      type: 'WELCOME',
      payload: {
        isConfirmWelcomeScreen: toggled
      }
    });
  };

  const createUserClient = async (companyName: string) => {
    try {
      const userId = state.user.userId;
      const { accessToken, refreshToken, user } = await httpPost(clientsURL, {
        name: companyName,
        userId
      });
      setSession(accessToken, refreshToken, user);
      const registerClientEvent = new RegistrationEvent('organization', {
        organization: companyName,
        organization_id: user.client.id
      });
      dispatch({
        type: 'UPDATE_COMPANY',
        payload: {
          user,
          isConfirmWelcomeScreen: false
        }
      });
    } catch (err) {
      return;
    }
  };

  const setData = (data: any) => {
    dispatch({
      type: 'SETDATACOMP',
      payload: {
        compData: data
      }
    });
  };

  const setPreventBack = (data: any) => {
    dispatch({
      type: 'SET_BACK_ACTION',
      payload: {
        isBack: data
      }
    });
  };
  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'JWT',
        login,
        loginWithGoogle,
        registerWithGoogle,
        logout,
        register,
        forgetPassword,
        updatePassword,
        userVerification,
        toggleWelcomeScreen,
        createUserClient,
        refreshUserToken,
        magicLinkLogin,
        setData,
        setPreventBack
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AuthContext;
